<template>
    <modal
        id="product-helper-modal"
        :centered="true"
        :title="$t('shop.productHelperModalTitle')"
        :show="modal.show"
        :close="modal.close"
        :closeable="modal.closeable"
        @modal-invisible="modal.show = modal.closeable = modal.close = false"
        size="modal-lg"
    >
        <template v-if="displayedQuestion">
            <loading-animation
                :visible="isOngoing"
                :always-show="false"
                preloader-container-id=""
                status-container-id=""
                :loading-text="$t('productHelperModal.loadingText')"
            ></loading-animation>
            <div class="modal-body">
                <template v-if="resultFound === null">
                    <div class="mb-1">
                        <h5 v-html="$t(displayedQuestion.question)"></h5>
                        <small v-html="$t(displayedQuestion.question_hint)"></small>
                    </div>
                </template>
                <div class="form-group">
                    <div class="row justify-content-center text-center">
                        <template v-if="resultFound === null">
                            <div class="col-12 col-md-6 mb-2" v-for="answerObj in displayedQuestion.answers" v-bind:key="answerObj.id">
                                <div class="checkbox-button-container mb-2" @click="selectAnswer(answerObj)">
                                    <input
                                        type="radio"
                                        name="answer"
                                        :value="answerObj.id"
                                        :id="answerObj.id"
                                        class="checkbox"
                                    />
                                    <label
                                        :for="answerObj['vehicle']"
                                        class="mb-0 shop-product-card alternative bg-white p-2 rounded text-center text-reset-case"
                                        v-bind:style="[!displayedQuestion.default_question ? {'padding-bottom' : '0.5rem !important'} : {}]"
                                    >
                                        <div class="h5 mr-1 shop-vehicle-icon" v-if="displayedQuestion.default_question">
                                            <template v-if="answerObj.use_vehicle_icon_img">
                                                <img
                                                    :src="vehicleIconUri(answerObj.vehicle_icon_img)"
                                                    alt="Vehicle"
                                                    height="auto"
                                                    width="25px"
                                                />
                                            </template>
                                            <template v-else>
                                                <i
                                                    :class="answerObj.vehicle_icon"
                                                    class="border-0 mt-0"
                                                >

                                                </i>
                                            </template>
                                        </div>
                                        <h6 v-bind:style="[!displayedQuestion.default_question ? {'margin' : '0 !important'} : {}]">{{ $t(answerObj.answer) }}</h6>
                                    </label>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="resultFound === true">
                            <div class="col-12 mb-3">
                                <div class="h5 mt-1 shop-vehicle-icon">
                                    <img
                                        :src="vehicleInfoImg(result.vehicle_type)"
                                        alt="Vehicle"
                                        height="auto"
                                        width="25px"
                                    />
                                </div>
                                <div class="h6">{{ $t('vehicleTypes.' + result.vehicle_type) }}</div>
                                <div><small>{{ $t('vehicleTypes.' + result.vehicle_type + '_description') }}</small></div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="col-12 mb-3">
                                <div class="h6">
                                    {{ $t(result.special_answer) }}
                                </div>
                            </div>
                        </template>

                        <template v-if="error">
                            <div class="col-12 mt-2">
                                <div class="alert alert-danger">
                                    <i class="mdi mdi-alert-circle"></i> <span v-html="$t('productHelperModal.failed')"></span>
                                </div>
                            </div>
                        </template>

                        <div class="col-12 mt-2" v-if="!displayedQuestion.default_question">
                            <button
                                type="button"
                                class="btn btn-primary mb-2"
                                @click="getPreviousQuestion()"
                            >{{ $t('productHelperModal.previousQuestion') }}</button>
                            <button
                                v-if="resultFound"
                                type="button"
                                class="btn btn-primary mb-2"
                                @click="getSelectedVehicleUri()"
                            >{{ $t('productHelperModal.confirmVignette') }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>

export default {
    name: 'ProductHelperModal',

    props: {
        questions: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            modal: {
                show: false,
                close: false,
                closeable: true,
            },
            isOngoing: false,
            displayedQuestion: [],
            questionsTrace: [],
            resultFound: null,
            result: [],
            error: false,
        };
    },

    mounted() {
        this.resetQuestions();
    },

    methods: {
        /**
         * Handle the visibility change of the modal.
         *
         * @returns {void}
         */
        showModal() {
            this.modal.closeable = true;
            this.modal.close = false;
            this.modal.show = true;
            if (!this.isOngoing) {
                this.resetQuestions();
            }
        },

        /**
         * Handle the hiding from the visible modal.
         *
         * @returns {void}
         */
        hideModal() {
            if (!this.modal.show) {
                return;
            }

            this.modal.closeable = true;
            this.$nextTick(() => {
                this.modal.close = true;
            });
        },

        /**
         * Determine the icon to use for displaying the vignette option.
         *
         * @param {string} img
         *
         * @returns {?string}
         */
        vehicleInfoImg(img) {
            if (img === null || img === '') {
                return null;
            }

            return '/images/vehicles/vehicle-icon-' + img + '.png';
        },

        /**
         * @param path
         *
         * @returns {?string}
         */
        vehicleIconUri(path) {
            if (path === null || path === '') {
                return null;
            }

            return path.charAt(0) === '/' ? path : '/' + path;
        },

        selectAnswer(selectedAnswer) {
            // Check if the selected answer has already a solution or a special answer.
            if (selectedAnswer.vehicle_type) {
                this.result = selectedAnswer;
                this.resultFound = true;
                return;
            } else if (selectedAnswer.special_answer) {
                this.result = selectedAnswer;
                this.resultFound = false;
                return;
            }

            // Save the previous question for the stacktrace.
            const previousQuestion = this.displayedQuestion;

            // Get the next question.
            this.displayedQuestion = this.getNextQuestion(selectedAnswer.next_question_id);

            if (!this.displayedQuestion.default_question) {
                this.questionsTrace.push(previousQuestion);
            }
        },

        getNextQuestion(nextQuestionId) {
            let q = [];

            this.questions.forEach((question) => {
                if (question.id === nextQuestionId) {
                    q = question;
                }
            });

            return q;
        },

        getPreviousQuestion() {
            if (this.resultFound === true || this.resultFound === false) {
                this.resultFound = null;

                return;
            } else if (this.error) {
                this.error = false;
            }

            const previous = this.questionsTrace[this.questionsTrace.length - 1];
            this.questionsTrace.pop();

            this.displayedQuestion = previous;
        },

        resetQuestions() {
            this.displayedQuestion = this.questions[0];
            this.questionsTrace = [];
            this.resultFound = null;
            this.result = [];
        },

        getSelectedVehicleUri() {
            if (this.resultFound !== true) {
                return;
            }

            // Avoid the submitting of multiple requests.
            if (this.isOngoing) {
                return;
            }

            this.isOngoing = true;
            axios.get(`/${this.$language}/productHelper/${this.result.vehicle_type}`).then((response) => {
                if (!response.data.success) {
                    this.isOngoing = false;
                    this.error = true;
                    return;
                }

                setTimeout(() => {
                    this.isOngoing = false;

                    // Redirect to the new url
                    window.location.href = new URL(response.data.uri, window.location.href).href;
                }, 10);
            }).catch(() => {
                this.isOngoing = false;
                this.error = true;
            });
        },
    },
};
</script>

<style lang="scss">
#product-helper-modal {
    .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999999;

        background: rgba(255, 255, 255, 0.8);
    }
    .loader-container .preloader .status {
        .spinner {
            position: relative;
            text-align: center;

            .spinner-inner {
                position: relative;
                margin: auto auto 1rem;

                .double-bounce1,
                .double-bounce2 {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;

                    background-color: #2f55d4;
                    opacity: 0.6;

                    -webkit-animation: sk-bounce 2s infinite ease-in-out;
                    animation: sk-bounce 2s infinite ease-in-out;
                }

                .double-bounce2 {
                    -webkit-animation-delay: -1s;
                    animation-delay: -1s;
                }

                width: 2.5rem;
                height: 2.5rem;
            }

            .spinner-text {
                font-weight: bolder;
            }
        }

        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
}
</style>
